import { createTheme, alpha, type Theme } from "@mui/material/styles";
import { radius, fontFamilyUI, typeScale } from "@karda-investigator/design-tokens";
import "./augmentations";
import { lightPalette, darkPalette } from "./palette";

export type ColorMode = "light" | "dark";

/**
 * The KARDA MUI theme. Components should lean on these defaults — palette
 * tokens, the type scale, and component styleOverrides — instead of
 * re-declaring values in sx.
 */
export function buildKardaMuiTheme(mode: ColorMode): Theme {
  return createTheme({
    palette: mode === "light" ? lightPalette : darkPalette,
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536, xxl: 2560, uhd: 3840 },
    },
    // Stays at MUI's default 4: numeric borderRadius values in sx are
    // MULTIPLIERS of theme.shape.borderRadius, and ~325 existing sx sites
    // (borderRadius: 2/3/4) were authored against base 4. The 8/12px design
    // radii are applied as px via the component styleOverrides below.
    shape: { borderRadius: 4 },
    typography: {
      fontFamily: fontFamilyUI,
      ...typeScale,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            // Fluid root font: 16px base scaling to 22px on 4K displays.
            fontSize: "clamp(16px, calc(14px + 0.2vw), 22px)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: radius.md,
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }),
          outlined: ({ theme }) => ({
            borderColor: alpha(theme.palette.text.primary, 0.3),
            color: theme.palette.text.primary,
          }),
        },
        variants: [
          {
            props: { variant: "gradient" },
            style: ({ theme }) => ({
              color: theme.palette.primary.contrastText,
              background: theme.palette.gradient.brand,
              "&:hover": { background: theme.palette.gradient.brandHover },
              "&.Mui-disabled": {
                background: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }),
          },
        ],
      },
      // Keyboard users get the same visible focus ring on icon buttons and
      // interactive cards as on buttons.
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            // The radius.md card default the token package documents. Kept off
            // MuiPaper deliberately: a global Paper radius would round Drawers
            // and Menus and fight the MuiDialog paper override below.
            borderRadius: radius.md,
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          outlined: ({ theme }) => ({
            borderColor: alpha(theme.palette.text.primary, 0.3),
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: radius.lg },
        },
      },
    },
  });
}
